import type { CompanyProfile } from '@/modules/company-profile/services/companyProfile'
import {
  type TempWorkerPaymentSlipAdvance,
  type TempWorkerPaymentSlipSettlement,
} from '@/modules/temp-worker/services/tempWorker'
import {
  BILL_LOCATION_LABELS,
  type BillLocationValue,
} from '@/constants/bill-location'
import { formatPrice } from '@/utils/number'

function parseYmd(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}

function formatWorkDateYmd(ymd: string, locale: string) {
  return parseYmd(ymd).toLocaleDateString(locale === 'th' ? 'th-TH' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function formatDateTime(iso: string, locale: string) {
  return new Date(iso).toLocaleString(locale === 'th' ? 'th-TH' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function locationLabel(loc: string | null | undefined): string {
  if (!loc) return '—'
  if (loc in BILL_LOCATION_LABELS) {
    return BILL_LOCATION_LABELS[loc as BillLocationValue]
  }
  return loc
}

type Labels = {
  slip_settlement_title: string
  slip_advance_title: string
  worker_name: string
  worker_position: string
  worker_phone: string
  work_period: string
  col_date: string
  col_location: string
  col_hours: string
  col_pay: string
  col_note: string
  gross_wage: string
  credit_applied: string
  net_cash: string
  remaining_credit: string
  total_row: string
  paid_at: string
  advance_amount: string
  note: string
  signature_receiver: string
  signature_payer: string
  baht: string
}

type Props = {
  company: CompanyProfile | null | undefined
  slip: TempWorkerPaymentSlipSettlement | TempWorkerPaymentSlipAdvance
  labels: Labels
  locale: string
}

function SlipCompanyHeader({
  company,
  docTitle,
}: {
  company: CompanyProfile | null | undefined
  docTitle: string
}) {
  const companyName = company?.legal_name?.trim() || '—'
  const companyAddress = company?.address?.trim() || ''
  const companyPhone = company?.phone?.trim() || ''

  return (
    <div className="slip-company-header border-b border-gray-300 pb-3 text-center">
      <p className="text-base font-bold">{companyName}</p>
      {companyAddress ? <p className="mt-1 text-[11px]">{companyAddress}</p> : null}
      {companyPhone ? <p className="text-[11px]">โทร {companyPhone}</p> : null}
      <p className="mt-3 text-sm font-semibold">{docTitle}</p>
    </div>
  )
}

export function TempWorkerPaymentSlipDocument({ company, slip, labels, locale }: Props) {
  if (slip.kind === 'advance') {
    return (
      <article
        id="slip-print-root"
        className="slip-print-root mx-auto box-border w-[148mm] max-w-[148mm] bg-white p-4 text-[11px] leading-relaxed text-gray-900 shadow-sm print:shadow-none"
      >
        <SlipCompanyHeader company={company} docTitle={labels.slip_advance_title} />

        <section className="mt-4 space-y-2">
          <Row label={labels.worker_name} value={slip.worker.name} />
          {slip.worker.position ? (
            <Row label={labels.worker_position} value={slip.worker.position} />
          ) : null}
          {slip.worker.phone ? <Row label={labels.worker_phone} value={slip.worker.phone} /> : null}
          <Row label={labels.paid_at} value={formatDateTime(slip.txn.created_at, locale)} />
          <Row
            label={labels.advance_amount}
            value={`${formatPrice(slip.txn.amount_baht)} ${labels.baht}`}
            bold
          />
          {slip.txn.note ? <Row label={labels.note} value={slip.txn.note} /> : null}
        </section>

        <SignatureBlock labels={labels} />
      </article>
    )
  }

  const totalPay = slip.sessions.reduce((s, row) => s + (row.pay_baht ?? 0), 0)

  return (
    <article
      id="slip-print-root"
      className="slip-print-root mx-auto box-border w-[148mm] max-w-[148mm] bg-white p-4 text-[11px] leading-relaxed text-gray-900 shadow-sm print:shadow-none"
    >
      <SlipCompanyHeader company={company} docTitle={labels.slip_settlement_title} />

      <section className="mt-3 grid gap-1">
        <Row label={labels.worker_name} value={slip.worker.name} />
        {slip.worker.position ? (
          <Row label={labels.worker_position} value={slip.worker.position} />
        ) : null}
        {slip.worker.phone ? <Row label={labels.worker_phone} value={slip.worker.phone} /> : null}
        <Row
          label={labels.work_period}
          value={`${formatWorkDateYmd(slip.period.start, locale)} – ${formatWorkDateYmd(slip.period.end, locale)}`}
        />
        <Row label={labels.paid_at} value={formatDateTime(slip.txn.created_at, locale)} />
      </section>

      <table className="mt-4 w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-gray-800">
            <th className="py-2 pr-2 text-left font-semibold">{labels.col_date}</th>
            <th className="py-2 pr-2 text-left font-semibold">{labels.col_location}</th>
            <th className="py-2 pr-2 text-right font-semibold">{labels.col_hours}</th>
            <th className="py-2 pr-2 text-right font-semibold">{labels.col_pay}</th>
            <th className="py-2 text-left font-semibold">{labels.col_note}</th>
          </tr>
        </thead>
        <tbody>
          {slip.sessions.map((row) => (
            <tr key={row.id} className="border-b border-gray-200">
              <td className="py-1.5 pr-2">{formatWorkDateYmd(row.work_date, locale)}</td>
              <td className="py-1.5 pr-2">{locationLabel(row.location)}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {row.hours != null ? row.hours.toFixed(2) : '—'}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{formatPrice(row.pay_baht ?? 0)}</td>
              <td className="max-w-[4rem] break-words py-1.5 text-left">
                {row.note?.trim() ? row.note : '—'}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-gray-800 font-semibold">
            <td className="py-2 pr-2" colSpan={4}>
              {labels.total_row}
            </td>
            <td className="py-2 text-right tabular-nums">{formatPrice(totalPay)}</td>
          </tr>
        </tbody>
      </table>

      <section className="mt-4 ml-auto w-full max-w-xs space-y-1.5">
        <SummaryRow
          label={labels.gross_wage}
          value={`${formatPrice(slip.summary.gross_wage_baht)} ${labels.baht}`}
        />
        <SummaryRow
          label={labels.credit_applied}
          value={`${formatPrice(slip.summary.credit_applied_baht)} ${labels.baht}`}
        />
        <SummaryRow
          label={labels.net_cash}
          value={`${formatPrice(slip.summary.net_cash_paid_baht)} ${labels.baht}`}
          bold
        />
        {slip.summary.remaining_credit_baht != null ? (
          <SummaryRow
            label={labels.remaining_credit}
            value={`${formatPrice(slip.summary.remaining_credit_baht)} ${labels.baht}`}
          />
        ) : null}
      </section>

      <SignatureBlock labels={labels} />
    </article>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex gap-2">
      <span className="min-w-[7rem] text-gray-600">{label}</span>
      <span className={bold ? 'font-bold' : undefined}>{value}</span>
    </div>
  )
}

function SummaryRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-100 pb-1">
      <span className="text-gray-600">{label}</span>
      <span className={bold ? 'text-base font-bold' : 'tabular-nums'}>{value}</span>
    </div>
  )
}

function SignatureBlock({ labels }: { labels: Labels }) {
  return (
    <div className="mt-8 grid grid-cols-2 gap-6">
      <div>
        <p className="text-gray-600">{labels.signature_receiver}</p>
        <div className="mt-10 border-b border-gray-400" />
      </div>
      <div>
        <p className="text-gray-600">{labels.signature_payer}</p>
        <div className="mt-10 border-b border-gray-400" />
      </div>
    </div>
  )
}
